import { useCallback, useEffect, useState, type FormEvent } from "react";

import { useNavigate } from "react-router";

import { Field, Section, fromMinor, input, toMinor } from "@/components/form";
import { ColourEditor, FeaturePicker } from "@/components/product-media";
import { fetchCategories } from "@/lib/api/catalogue";
import * as products from "@/lib/api/products";
import { ApiError } from "@/lib/api-client";
import type { ApiCategory, ProductColour } from "@/lib/api/types";

/**
 * New product (ADMIN.md §9, phase 5c).
 *
 * ── Why creating and photographing are two steps ───────────────────────────
 *
 * An image's storage key is derived from the product's slug, so there is no
 * upload to make until the row exists. Rather than pretend otherwise with a
 * staging area, this form creates the product and then sends the operator
 * straight to its gallery — and the product is created **DRAFT**, so the
 * window between "it exists" and "it has photographs" is invisible to the
 * storefront. Publishing is the deliberate last click, made by somebody looking
 * at a finished page.
 *
 * ── Why the slug is on this screen and nowhere else ────────────────────────
 *
 * It is the product's permanent URL. arrow-fe bakes one HTML file per slug at
 * build time, so renaming one 404s a live page and orphans every link to it —
 * which is why the API has no way to change it afterwards. This is the only
 * moment it can be chosen, so it is shown plainly, derived from the name as a
 * starting point, and never silently rewritten.
 */

const REQUIRED = ["name", "eyebrow", "spec", "description", "specSheet"] as const;

export function NewProductRoute() {
  const navigate = useNavigate();

  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  /** Null until the operator edits it; before that the name drives it. */
  const [slugOverride, setSlugOverride] = useState<string | null>(null);
  const [eyebrow, setEyebrow] = useState("");
  const [spec, setSpec] = useState("");
  const [category, setCategory] = useState("");
  const [price, setPrice] = useState("");
  const [mrp, setMrp] = useState("");
  const [discountLabel, setDiscountLabel] = useState("");
  const [badge, setBadge] = useState("");
  const [description, setDescription] = useState("");
  const [specSheet, setSpecSheet] = useState("");
  const [lowStockThreshold, setLowStockThreshold] = useState("5");
  const [hsnCode, setHsnCode] = useState("");
  const [gstRate, setGstRate] = useState("");
  /**
   * Blank by default, and left blank is fine: a product is usually entered
   * before anyone has put it on a scale. `buildParcel` refuses to book it and
   * names the slug, which is a better prompt than a guessed weight.
   */
  const [weightGrams, setWeightGrams] = useState("");
  const [carton, setCarton] = useState({ lengthMm: "", breadthMm: "", heightMm: "" });
  const [colours, setColours] = useState<ProductColour[]>([]);
  const [features, setFeatures] = useState<string[]>([]);

  const slug = slugOverride ?? products.slugify(name);

  useEffect(() => {
    const controller = new AbortController();

    void fetchCategories(controller.signal)
      .then((rows) => {
        setCategories(rows);
        // Preselect, so the commonest case is one fewer decision. The operator
        // still sees which one it is.
        setCategory((current) => current || (rows[0]?.slug ?? ""));
      })
      .catch(() => {
        if (!controller.signal.aborted) setError("Could not load the category list.");
      });

    return () => controller.abort();
  }, []);

  const priceMinor = toMinor(price);
  const mrpMinor = toMinor(mrp);

  const filled = {
    name,
    eyebrow,
    spec,
    description,
    specSheet,
  };

  const missing = REQUIRED.filter((key) => !filled[key].trim());

  const ready =
    missing.length === 0 &&
    slug.length >= 2 &&
    category !== "" &&
    priceMinor !== null &&
    mrpMinor !== null &&
    priceMinor <= mrpMinor &&
    discountLabel.trim() !== "";

  const save = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      /**
       * `ready` already proves `priceMinor` and `mrpMinor` are numbers, and
       * TypeScript narrows them through it — so neither needs an assertion
       * below. If that check ever leaves `ready`, this stops compiling, which
       * is the point of not asserting.
       */
      if (!ready || busy) return;

      setBusy(true);
      setError(null);

      try {
        const created = await products.createProduct({
          slug,
          name: name.trim(),
          eyebrow: eyebrow.trim(),
          spec: spec.trim(),
          category,
          priceMinor,
          mrpMinor,
          discountLabel: discountLabel.trim(),
          badge: badge.trim(),
          description: description.trim(),
          specSheet: specSheet.trim(),
          lowStockThreshold: Number(lowStockThreshold) || 0,
          hsnCode: hsnCode.trim() || null,
          gstRateBp: gstRate.trim() ? Math.round(Number(gstRate) * 100) : null,
          weightGrams: weightGrams.trim() ? Number(weightGrams) : null,
          // All three or none — the API refuses a partial carton, so a
          // half-filled row is sent as no carton at all rather than as a 400.
          ...(carton.lengthMm.trim() && carton.breadthMm.trim() && carton.heightMm.trim()
            ? {
                lengthMm: Number(carton.lengthMm),
                breadthMm: Number(carton.breadthMm),
                heightMm: Number(carton.heightMm),
              }
            : {}),
          colours: colours.filter((colour) => colour.name.trim()),
          features,
        });

        /**
         * Straight to the product, with its gallery open. The next thing to do
         * is always "add the photographs", and making the operator find the row
         * they just created in a paginated table would be a small insult.
         */
        void navigate(`/products?open=${encodeURIComponent(created.slug)}`);
      } catch (thrown) {
        setError(
          thrown instanceof ApiError ? thrown.message : "That product could not be created.",
        );
        setBusy(false);
      }
    },
    [
      ready,
      busy,
      slug,
      name,
      eyebrow,
      spec,
      category,
      priceMinor,
      mrpMinor,
      discountLabel,
      badge,
      description,
      specSheet,
      lowStockThreshold,
      hsnCode,
      gstRate,
      weightGrams,
      carton,
      colours,
      features,
      navigate,
    ],
  );

  return (
    <form onSubmit={(event) => void save(event)} className="mx-auto max-w-3xl">
      <header className="mb-4">
        <h1 className="text-[18px] font-bold text-ink">New product</h1>
        <p className="mt-1 text-[12px] text-faint">
          Created as a <strong className="font-semibold text-body">draft</strong>. It reaches the
          storefront only once you set it Active, which is the step to take after its photographs
          are up.
        </p>
      </header>

      <Section title="Identity">
        <Field label="Name">
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Arrow Airdopes T5"
            className={input}
          />
        </Field>

        <Field
          label="URL slug"
          hint="Permanent. The storefront bakes one page per slug, so this cannot be changed later — a rename would 404 the live page."
        >
          <input
            type="text"
            value={slug}
            onChange={(event) => setSlugOverride(event.target.value)}
            placeholder="arrow-airdopes-t5"
            className={input}
          />
        </Field>

        {slug ? (
          <p className="mt-1 text-[11px] text-faint">
            arrowindia.com/products/<span className="font-mono text-body">{slug}</span>/
          </p>
        ) : null}

        <Field label="Eyebrow" hint="The small label above the name on a card — TWS EARBUDS">
          <input
            type="text"
            value={eyebrow}
            onChange={(event) => setEyebrow(event.target.value)}
            className={input}
          />
        </Field>

        <Field label="Card spec" hint="The one-line spec under the name on a card">
          <input
            type="text"
            value={spec}
            onChange={(event) => setSpec(event.target.value)}
            placeholder="400 Hrs Battery · BT 5.4 · Dolby Sound"
            className={input}
          />
        </Field>

        <Field label="Category">
          <select
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            className={input}
          >
            {categories.map((entry) => (
              <option key={entry.slug} value={entry.slug}>
                {entry.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Badge" hint="BESTSELLER, NEW, or leave empty">
          <input
            type="text"
            value={badge}
            onChange={(event) => setBadge(event.target.value)}
            className={input}
          />
        </Field>
      </Section>

      <Section title="Price">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Selling price (₹)">
            <input
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              className={input}
            />
          </Field>

          <Field label="MRP (₹)">
            <input
              type="number"
              min="0"
              step="0.01"
              value={mrp}
              onChange={(event) => setMrp(event.target.value)}
              className={input}
            />
          </Field>
        </div>

        <Field
          label="Discount label"
          hint="Stated, never computed. The distributor sheet's wording is what the storefront shows — it says 25% off where the MRP arithmetic gives 20%."
        >
          <input
            type="text"
            value={discountLabel}
            onChange={(event) => setDiscountLabel(event.target.value)}
            placeholder="25% off"
            className={input}
          />
        </Field>

        {priceMinor !== null && mrpMinor !== null && priceMinor > mrpMinor ? (
          <p className="mt-1 text-[12px] text-bad">The selling price is above the MRP.</p>
        ) : priceMinor !== null && mrpMinor !== null && mrpMinor > 0 ? (
          <p className="mt-1 text-[11px] text-faint">
            Saving ₹{fromMinor(mrpMinor - priceMinor)} — the arithmetic gives{" "}
            {Math.round((1 - priceMinor / mrpMinor) * 100)}% off.
          </p>
        ) : null}
      </Section>

      <Section title="Copy">
        <Field label="Description">
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={4}
            className={input}
          />
        </Field>

        <Field label="Spec sheet" hint="The long ` · `-joined spec line on the product page">
          <textarea
            value={specSheet}
            onChange={(event) => setSpecSheet(event.target.value)}
            rows={3}
            className={input}
          />
        </Field>
      </Section>

      <ColourEditor colours={colours} onChange={setColours} />

      <FeaturePicker selected={features} onChange={setFeatures} />

      <Section title="Stock and tax">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Low-stock threshold" hint="Drives the restock report">
            <input
              type="number"
              min="0"
              value={lowStockThreshold}
              onChange={(event) => setLowStockThreshold(event.target.value)}
              className={input}
            />
          </Field>

          <Field label="HSN code" hint="4–8 digits. Without it, orders cannot be invoiced">
            <input
              type="text"
              value={hsnCode}
              onChange={(event) => setHsnCode(event.target.value)}
              className={input}
            />
          </Field>

          <Field label="GST rate (%)" hint="18, 12, 5 — stored as basis points">
            <input
              type="number"
              min="0"
              max="28"
              step="0.01"
              value={gstRate}
              onChange={(event) => setGstRate(event.target.value)}
              className={input}
            />
          </Field>
        </div>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field label="Shipping weight (g)" hint="Packaged. Without it, no parcel can be booked">
            <input
              type="number"
              min="1"
              value={weightGrams}
              onChange={(event) => setWeightGrams(event.target.value)}
              className={input}
            />
          </Field>

          <Field label="Carton L×B×H (mm)" hint="All three, or none">
            <div className="flex gap-1.5">
              {(["lengthMm", "breadthMm", "heightMm"] as const).map((side) => (
                <input
                  key={side}
                  type="number"
                  min="1"
                  placeholder={side === "lengthMm" ? "L" : side === "breadthMm" ? "B" : "H"}
                  value={carton[side]}
                  onChange={(event) =>
                    setCarton((current) => ({ ...current, [side]: event.target.value }))
                  }
                  className={input}
                />
              ))}
            </div>
          </Field>
        </div>

        <p className="mt-2 text-[11px] text-faint">
          Opening stock is not set here. A new product starts at zero and takes its first units
          through the Stock screen, so the ledger reconciles from the first unit rather than the
          second.
        </p>
      </Section>

      {error ? (
        <p className="mb-3 rounded-lg border border-bad bg-bad-tint px-3 py-2 text-[12px] text-bad">
          {error}
        </p>
      ) : null}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={!ready || busy}
          className="rounded-lg bg-ink px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
        >
          {busy ? "Creating…" : "Create draft"}
        </button>

        <button
          type="button"
          onClick={() => void navigate("/products")}
          className="rounded-lg border border-line px-4 py-2 text-[13px] font-medium text-body hover:bg-canvas"
        >
          Cancel
        </button>

        {missing.length > 0 ? (
          <span className="text-[11px] text-faint">Still needed: {missing.join(", ")}</span>
        ) : null}
      </div>
    </form>
  );
}
